import type {
	AssetSheetRowObjectReport,
	AssetSheetRowProblem,
	AssetSheetRowReportDTO,
	AssetSheetTag,
	VeevaVaultAssetFileDTO,
	VeevaVaultAssetImportDTO,
} from '@atlas/types'
import { AssetSheetDerivedRowStatus, AssetSheetLevel, AxisCode, VeevaVaultFileImportStatus } from '@atlas/types'
import { Badge, type BadgeProps, Button, Tag } from '@atlas/ui-primitives'
import { Check, Loader, X } from 'lucide-react'

export interface RowDetailProps {
	row: AssetSheetRowReportDTO
	/** Queue an asset's finally failed and stalled files again. */
	onRetry: (assetCode: string) => void
}

/**
 * What a row needs an administrator to know: why a blocked row is blocked, or each asset it
 * references with its download, its files and the exact tags the row adds to them.
 */
export function RowDetail({ row, onRetry }: RowDetailProps) {
	if (row.derivedStatus === AssetSheetDerivedRowStatus.BLOCKED) return <BlockedReasons problems={row.problems} />
	if (row.derivedStatus === AssetSheetDerivedRowStatus.REMOVED)
		return (
			<p className="text-ds-sm text-ds-text-muted">
				This row is no longer in the workbook. The tags it added to its files are removed.
			</p>
		)
	const objects = row.objects.filter((o) => !o.removed)
	if (objects.length === 0) return <p className="text-ds-sm text-ds-text-muted">This row references no assets.</p>
	return (
		<div className="flex flex-col gap-ds-4">
			<h3 className="text-ds-xs font-semibold uppercase tracking-wide text-ds-text-muted">Assets</h3>
			{objects.map((o) => (
				<AssetBlock
					key={`${o.column}:${o.sourceKey}`}
					object={o}
					assetImport={row.imports[o.sourceKey] ?? null}
					onRetry={onRetry}
				/>
			))}
			<p className="text-ds-xs text-ds-text-muted">
				These are the tags this row adds. A file another row also references has that row's tags too, and tags added by
				hand are kept.
			</p>
		</div>
	)
}

function BlockedReasons({ problems }: { problems: AssetSheetRowProblem[] }) {
	return (
		<div className="flex flex-col gap-ds-3">
			<h3 className="text-ds-xs font-semibold uppercase tracking-wide text-ds-text-muted">Why this row is blocked</h3>
			<ul className="flex flex-col divide-y divide-ds-border">
				{problems.map((p) => (
					<li key={`${p.column}:${p.text}`} className="grid gap-ds-1 py-ds-2 text-ds-sm sm:grid-cols-[12rem_14rem_1fr]">
						<span className="text-ds-text">{p.column}</span>
						<span className="font-ds-mono wrap-anywhere text-ds-text">"{p.text}"</span>
						<span className="text-ds-text-muted">{p.reason}</span>
					</li>
				))}
			</ul>
			<p className="text-ds-xs text-ds-text-muted">
				Fix the cell in the workbook, or add the name to the taxonomy for the row's country, then sync. Until then the
				row keeps the tags it had.
			</p>
		</div>
	)
}

type Look = { label: string; variant: BadgeProps['variant'] }

/** Where an asset's download and tagging stand, from its request, its import and the files tagged. */
export function assetState(object: AssetSheetRowObjectReport, assetImport: VeevaVaultAssetImportDTO | null): Look {
	if (object.requestError) return { label: 'Refused', variant: 'danger' }
	const files = assetImport?.files ?? []
	if (files.length === 0) return { label: 'Not started', variant: 'muted' }
	const has = (status: VeevaVaultFileImportStatus) => files.some((f) => f.status === status)
	if (has(VeevaVaultFileImportStatus.FAILURE)) return { label: 'Failed', variant: 'danger' }
	if (has(VeevaVaultFileImportStatus.PENDING) || has(VeevaVaultFileImportStatus.DOWNLOADING))
		return { label: 'Downloading', variant: 'warning' }
	if (files.some((f) => !isTagged(object, f))) return { label: 'Tagging', variant: 'warning' }
	return { label: 'Done', variant: 'success' }
}

const isTagged = (object: AssetSheetRowObjectReport, file: VeevaVaultAssetFileDTO) =>
	file.fileId !== null && object.tagged[file.fileId] !== undefined

function AssetBlock({
	object,
	assetImport,
	onRetry,
}: {
	object: AssetSheetRowObjectReport
	assetImport: VeevaVaultAssetImportDTO | null
	onRetry: (assetCode: string) => void
}) {
	const state = assetState(object, assetImport)
	const files = assetImport?.files ?? []
	const canRetry = files.some((f) => f.retryable)
	const fileType = object.tags.values.find((t) => t.axis === AxisCode.FILE_TYPE)?.label ?? object.fileType
	return (
		<section
			aria-label={object.sourceKey}
			className="flex flex-col gap-ds-2 border-t border-ds-border pt-ds-3 first-of-type:border-t-0 first-of-type:pt-0"
		>
			<div className="flex flex-wrap items-center gap-ds-2">
				<span className="font-ds-mono text-ds-sm text-ds-text">{object.sourceKey}</span>
				<span className="text-ds-sm text-ds-text-muted">
					{object.column} · {fileType}
				</span>
				<span className="flex-1" />
				<Badge variant={state.variant} size="sm">
					{state.label}
				</Badge>
				{canRetry && (
					<Button variant="secondary" size="sm" onPress={() => onRetry(object.sourceKey)}>
						Retry
					</Button>
				)}
			</div>
			{object.requestError ? (
				<p className="text-ds-sm text-ds-danger">
					{object.requestError.reason} Fix the code in the workbook or in Vault, then sync.
				</p>
			) : (
				<>
					<TagList tags={object.tags.values} applied={object.tags.applied} />
					{files.length === 0 ? (
						<p className="text-ds-xs text-ds-text-muted">Waiting for the Vault import to start.</p>
					) : (
						<ul className="flex flex-col gap-ds-1">
							{files.map((f) => (
								<FileLine key={f.id} file={f} tagged={isTagged(object, f)} />
							))}
						</ul>
					)}
				</>
			)}
		</section>
	)
}

const AXIS_NAMES: Partial<Record<string, string>> = {
	[AxisCode.GEOGRAPHY]: 'Country',
	[AxisCode.SPECIES]: 'Species',
	[AxisCode.FILE_TYPE]: 'File type',
}

const axisName = (tag: AssetSheetTag) =>
	tag.axis === AxisCode.BRAND
		? tag.level === AssetSheetLevel.PRODUCT
			? 'Product'
			: 'Brand'
		: (AXIS_NAMES[tag.axis] ?? tag.axis)

function TagList({ tags, applied }: { tags: AssetSheetTag[]; applied: boolean }) {
	return (
		<div className="flex flex-wrap items-center gap-ds-1">
			<span className="mr-ds-1 text-ds-xs text-ds-text-muted">{applied ? 'Tagged with' : 'Will be tagged with'}</span>
			<ul className="contents" aria-label={applied ? 'Tags applied' : 'Tags to apply'}>
				{tags.map((t) => (
					<li key={`${t.axis}:${t.code}`}>
						<Tag variant="muted" size="sm" title={t.code}>
							<span className="mr-ds-1 text-ds-text-subtle">{axisName(t)}</span>
							{t.label}
						</Tag>
					</li>
				))}
			</ul>
		</div>
	)
}

function fileStatus(file: VeevaVaultAssetFileDTO, tagged: boolean) {
	switch (file.status) {
		case VeevaVaultFileImportStatus.SUCCESS:
			return tagged
				? { icon: <Check aria-hidden className="h-4 w-4 text-ds-success" />, text: 'Downloaded · tagged' }
				: { icon: <Loader aria-hidden className="h-4 w-4 text-ds-warning" />, text: 'Downloaded · waiting for tags' }
		case VeevaVaultFileImportStatus.FAILURE:
			return { icon: <X aria-hidden className="h-4 w-4 text-ds-danger" />, text: 'Failed' }
		case VeevaVaultFileImportStatus.DOWNLOADING:
			return { icon: <Loader aria-hidden className="h-4 w-4 text-ds-warning" />, text: 'Downloading' }
		default:
			return { icon: <Loader aria-hidden className="h-4 w-4 text-ds-text-subtle" />, text: 'Queued' }
	}
}

function FileLine({ file, tagged }: { file: VeevaVaultAssetFileDTO; tagged: boolean }) {
	const { icon, text } = fileStatus(file, tagged)
	const failed = file.status === VeevaVaultFileImportStatus.FAILURE
	return (
		<li className="grid grid-cols-[1rem_minmax(0,1fr)] items-center gap-x-ds-2 text-ds-sm sm:grid-cols-[1rem_minmax(0,1fr)_12rem]">
			{icon}
			{file.fileName ? (
				<span className="font-ds-mono wrap-anywhere text-ds-text">{file.fileName}</span>
			) : (
				<span className="text-ds-text">{file.file === 'source' ? 'Source file' : `${file.file} rendition`}</span>
			)}
			<span className="col-start-2 text-ds-text-muted sm:col-start-auto">{text}</span>
			{failed && file.lastError && <span className="col-start-2 text-ds-danger sm:col-end-4">{file.lastError}</span>}
		</li>
	)
}
